import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChatMessage } from '@citiguide/shared';

interface ChatTranscriptProps {
  messages: ChatMessage[];
  /** The visitor's text while its turn is in flight (not yet in the stored transcript). */
  pendingText: string | null;
  thinking: boolean;
  longWait: boolean;
}

/**
 * The conversation. Reply text is rendered as TEXT (React escapes it) -- never as HTML.
 * `role="log"` + polite live region so a screen reader announces each new reply.
 */
export default function ChatTranscript({ messages, pendingText, thinking, longWait }: ChatTranscriptProps) {
  const { t } = useTranslation();
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // jsdom has no scrollIntoView
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages.length, pendingText, thinking]);

  return (
    <div className="chat-transcript" role="log" aria-live="polite" aria-label={t('chat.title')}>
      {messages.map((m, i) => (
        <p key={i} className={`chat-message chat-message--${m.role}`}>
          {m.content}
        </p>
      ))}
      {pendingText && <p className="chat-message chat-message--user chat-message--pending">{pendingText}</p>}
      {thinking && (
        <p className="chat-thinking" role="status">
          {longWait ? t('chat.thinkingLong') : t('chat.thinking')}
        </p>
      )}
      <div ref={endRef} />
    </div>
  );
}
